import { useCallback } from 'react';
import { TreeNode } from '../../types/types';
import { findNode } from '../../utils/treeUtils';
import { updateSortOrderForAllChildPages } from '../../services/pageservice';
import { notification } from 'antd';
import type { TreeProps } from 'antd/es/tree';

interface UseTreeDropHandlerProps {
    data: TreeNode[];
    setData: (data: TreeNode[]) => void;
    setLoading: (loading: boolean) => void;
}

// Use Ant Design's official onDrop parameter type
type DropInfo = Parameters<NonNullable<TreeProps['onDrop']>>[0];

export const useTreeDropHandler = ({
    data,
    setData,
    setLoading,
}: UseTreeDropHandlerProps) => {
    const onDrop = useCallback((info: DropInfo) => {
        setLoading(true);

        const dragNode = (info.dragNode as any) as TreeNode;
        if (!dragNode) {
            setLoading(false);
            notification.error({
                message: 'Drag Error',
                description: 'Could not retrieve drag node data.',
                placement: 'bottomLeft',
            });
            return;
        }
        const dropNode = (info.node as any) as TreeNode;
        if (!dropNode) {
            setLoading(false);
            notification.error({
                message: 'Drop Error',
                description: 'Could not retrieve drop node data.',
                placement: 'bottomLeft',
            });
            return;
        }

        const dragKey = dragNode.key;
        const dropKey = dropNode.key;
        const dragParentKey = dragNode.parentPageGuid;
        const dropParentKey = dropNode.parentPageGuid;

        const dropNodeIsTheParent = dragParentKey !== dropKey;
        const draggedWithinSameParent = dragParentKey !== dropParentKey;

        if (dropNodeIsTheParent && draggedWithinSameParent) {
            setLoading(false);
            notification.warning({
                message: 'Invalid Drop',
                description: 'Child page can be reordered only within the same parent page.',
                placement: 'bottomLeft',
            });
            return;
        }

        if (!info.dropToGap && dragParentKey === dropParentKey && dragKey !== dropKey) {
            setLoading(false);
            notification.warning({
                message: 'Invalid Drop',
                description: 'Cannot drag a sibling directly onto another sibling.',
                placement: 'bottomLeft',
            });
            return;
        }

        const loop = (
            data: TreeNode[],
            key: string,
            callback: (item: TreeNode, index: number, arr: TreeNode[]) => void
        ) => {
            data.forEach((item, index, arr) => {
                if (item.key === key) {
                    return callback(item, index, arr);
                }
                if (item.children) {
                    return loop(item.children, key, callback);
                }
            });
        };

        const dataCopy = [...data];
        let dragObj: TreeNode;
        let dragIndex: number | undefined;

        loop(dataCopy, dragKey, (item, index, arr) => {
            dragIndex = index;
            arr.splice(index, 1);
            dragObj = item;
        });

        if (dragParentKey) {
            loop(dataCopy, dragParentKey, (item) => {
                let insertIndex: number;

                if (info.dropToGap) {
                    insertIndex = Math.max(0, info.dropPosition);
                    if (dragIndex !== undefined && insertIndex > dragIndex && item.children?.length) {
                        insertIndex--;
                    }
                } else {
                    insertIndex = 0;
                }

                item.children = item.children || [];
                item.children.splice(insertIndex, 0, dragObj);

                item.children.forEach((child, index) => {
                    if (child.data) child.data.sortOrder = index + 1;
                    child.sortOrder = index + 1;
                });
            });

            const parentNode = findNode(dataCopy, dragParentKey);
            if (parentNode && parentNode.children) {
                const sortOrderUpdates: Record<string, number> = {};
                parentNode.children.forEach((child, index) => {
                    sortOrderUpdates[child.key] = index + 1;
                });

                updateSortOrderForAllChildPages(sortOrderUpdates)
                    .then(() => {
                        setData(dataCopy);
                    })
                    .catch((error) => {
                        console.error('Error updating sort order:', error);
                        notification.error({
                            message: 'Update Failed',
                            description: 'Failed to update sort order. Please try again.',
                            placement: 'bottomLeft',
                        });
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }, [data, setData, setLoading]);

    return { onDrop };
};